import React, { useContext, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import CountUp from 'react-countup/build';
import { Alert, Badge, Card, CardBody, Col, Row, Spinner } from 'reactstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Link } from 'react-router-dom';
import ReactEchartsCore from 'echarts-for-react/lib/core';
import * as echarts from 'echarts/lib/echarts';
import { LinearGradient } from 'echarts/lib/util/graphic';
import 'echarts/lib/chart/bar';
import 'echarts/lib/chart/pie';
import 'echarts/lib/component/grid';
import 'echarts/lib/component/tooltip';
import 'echarts/lib/component/legend';
import { API_BASE_CANDIDATES } from '../../apiConfig';
import AppContext from '../../context/Context';
import { hasPermission } from '../../helpers/permissions';
import { formatSelectedPeriod } from '../../helpers/testPeriod';
import { getAuthHeaders, rgbaColor, themeColors } from '../../helpers/utils';

const createLinearGradient = (x, y, x2, y2, colorStops) => new LinearGradient(x, y, x2, y2, colorStops);

const diagnosticModules = [
  {
    title: 'Temperament tahlili',
    description: 'Talabaning tabiiy psixodinamik tipini aniqlash va individual yondashuvni shakllantirish.',
    icon: 'users'
  },
  {
    title: 'Kommunikativ va tashkilotchilik',
    description: 'Muloqot, jamoa bilan ishlash va tashabbus ko`rsatish salohiyatini baholash.',
    icon: 'comments'
  },
  {
    title: 'Motivatsiya diagnostikasi',
    description: 'Ta`lim, rivojlanish va shaxsiy maqsadlarga intilish darajasini aniqlash.',
    icon: 'bullseye'
  },
  {
    title: 'Bezovtalanish darajasi',
    description: 'Anksiozlik va psixoemotsional taranglik ko`rsatkichlarini aniqlash.',
    icon: 'exclamation-circle'
  },
  {
    title: 'Nevrasteniya shkalasi',
    description: 'Tezkor baholash orqali psixologik charchoq va zo`riqish belgilari kuzatiladi.',
    icon: 'bolt'
  },
  {
    title: 'Munosabatlar diagnostikasi',
    description: 'Talabaning ijtimoiy muhit, oila va guruh bilan aloqalari psixologik nuqtayi nazardan ko`riladi.',
    icon: 'handshake'
  },
  {
    title: 'Kasbga moyillik',
    description: 'Kasbiy qiziqishlar va yo`nalish tanlashdagi ichki moyillik aniqlanadi.',
    icon: 'briefcase'
  },
  {
    title: 'Psixologik pasport',
    description: 'Har bir talaba uchun kuchli va rivojlantirilishi kerak bo`lgan jihatlar bir joyga jamlanadi.',
    icon: 'id-card'
  }
];

const platformCapabilities = [
  {
    title: 'Onlayn qabul',
    description: 'Psixolog qabuliga yozilish va individual ishlash jarayonini boshqarish.',
    icon: 'calendar-check'
  },
  {
    title: 'Masofaviy testlar',
    description: 'So`rovnoma va diagnostika testlarini auditoriyadan tashqarida ham o`tkazish.',
    icon: 'laptop'
  },
  {
    title: 'Analitik panel',
    description: 'Psixolog uchun risk, temperament va guruhlar kesimida kengaytirilgan tahlil.',
    icon: 'chart-line'
  },
  {
    title: 'Dinamik monitoring',
    description: 'Vaqt o`tishi bilan psixologik holatdagi o`zgarishlarni kuzatish va solishtirish.',
    icon: 'sync-alt'
  }
];

const emptyState = {
  period: null,
  stats: {
    students: 0,
    groups: 0,
    subjects: 0,
    passport_coverage: 0,
    passport_filled: 0,
    results: 0,
    total_students: 0,
    total_groups: 0,
    total_faculties: 0,
    total_passport_filled: 0,
    total_passport_coverage: 0
  },
  risk_cards: [],
  temperament: [],
  faculties: [],
  course_results: [],
  nationality_results: [],
  recent_students: [],
  priority_alerts: [],
  user: null
};

const AnimatedCount = ({ value, prefix = '', decimals = 0, duration = 2.2, className }) => (
  <CountUp
    className={className}
    end={Number(value) || 0}
    duration={duration}
    separator=" "
    decimals={decimals}
    prefix={prefix}
  />
);

const AnimatedMetricValue = ({ value, suffix = '', prefix = '', duration = 2.2, className = '' }) => (
  <span className={`psi-animated-value ${className}`.trim()}>
    <AnimatedCount value={value} prefix={prefix} duration={duration} className="psi-animated-value__number" />
    {suffix ? <span className="psi-animated-value__suffix">{suffix}</span> : null}
  </span>
);

const MetricCard = ({ label, value, suffix, note, icon, tone, accent, index = 0 }) => (
  <Card className={`psi-metric-card psi-metric-card--${tone} psi-reveal`} style={{ animationDelay: `${index * 0.08}s` }}>
    <CardBody>
      <div className="psi-metric-card__head">
        <div>
          <span className="psi-metric-card__label">{label}</span>
          <strong className="psi-metric-card__value">
            <AnimatedMetricValue value={value} suffix={suffix} />
          </strong>
        </div>
        <span className="psi-metric-card__icon">
          <FontAwesomeIcon icon={icon} />
        </span>
      </div>
      <p className="psi-metric-card__note">{note}</p>
      {accent ? <div className="psi-metric-card__accent">{accent}</div> : null}
    </CardBody>
  </Card>
);

const PanelCard = ({ title, subtitle, action, children, className = '' }) => (
  <Card className={`psi-panel-card psi-reveal ${className}`.trim()}>
    <CardBody>
      <div className="psi-panel-card__head">
        <div>
          <h3>{title}</h3>
          {subtitle ? <p>{subtitle}</p> : null}
        </div>
        {action ? <div className="psi-panel-card__action">{action}</div> : null}
      </div>
      {children}
    </CardBody>
  </Card>
);

const SystemCard = ({ title, description, icon, index = 0 }) => (
  <Card className="psi-system-card psi-reveal" style={{ animationDelay: `${0.28 + index * 0.06}s` }}>
    <CardBody>
      <div className="psi-system-card__head">
        <span className="psi-system-card__icon">
          <FontAwesomeIcon icon={icon} />
        </span>
        <span className="psi-system-card__eyebrow">Faol metodika</span>
      </div>
      <h4>{title}</h4>
      <p>{description}</p>
    </CardBody>
  </Card>
);

const CapabilityCard = ({ title, description, icon, index = 0 }) => (
  <Card className="psi-capability-card psi-reveal" style={{ animationDelay: `${0.32 + index * 0.08}s` }}>
    <CardBody>
      <div className="psi-capability-card__icon">
        <FontAwesomeIcon icon={icon} />
      </div>
      <h4>{title}</h4>
      <p>{description}</p>
    </CardBody>
  </Card>
);

const IntroStatPill = ({ label, value, suffix, icon, tone, delay = 0 }) => (
  <div className={`psi-intro-pill psi-intro-pill--${tone} psi-reveal`} style={{ animationDelay: `${delay}s` }}>
    <span className="psi-intro-pill__icon">
      <FontAwesomeIcon icon={icon} />
    </span>
    <div className="psi-intro-pill__copy">
      <strong>
        <AnimatedMetricValue value={value} suffix={suffix} duration={2.4} className="psi-animated-value--compact" />
      </strong>
      <small>{label}</small>
    </div>
  </div>
);

const getTemperamentOption = (items, isDark) => {
  const textColor = isDark ? '#dbe7ff' : '#334155';

  return {
    animationDuration: 1200,
    animationEasing: 'cubicOut',
    color: ['#2563eb', '#0ea5e9', '#f59e0b', '#ef4444'],
    tooltip: {
      trigger: 'item'
    },
    legend: {
      bottom: 0,
      left: 'center',
      icon: 'circle',
      itemWidth: 10,
      itemHeight: 10,
      textStyle: { color: textColor }
    },
    series: [
      {
        type: 'pie',
        radius: ['46%', '76%'],
        center: ['50%', '42%'],
        itemStyle: {
          borderColor: isDark ? '#0f172a' : '#ffffff',
          borderWidth: 4
        },
        label: {
          color: textColor,
          formatter: '{b}\n{c}',
          fontWeight: 700
        },
        labelLine: {
          lineStyle: {
            color: isDark ? 'rgba(226,232,240,0.35)' : 'rgba(15,23,42,0.18)'
          }
        },
        data: items
      }
    ]
  };
};

const getFacultyOption = (items, isDark) => {
  const axisColor = isDark ? '#dbe7ff' : '#475569';
  const splitLineColor = isDark ? 'rgba(255,255,255,0.08)' : 'rgba(15,23,42,0.07)';

  return {
    animationDuration: 1100,
    animationEasing: 'quarticOut',
    grid: {
      left: 10,
      right: 10,
      top: 20,
      bottom: 20,
      containLabel: true
    },
    xAxis: {
      type: 'value',
      axisLabel: { color: axisColor },
      splitLine: { lineStyle: { color: splitLineColor } },
      axisTick: { show: false },
      axisLine: { show: false }
    },
    yAxis: {
      type: 'category',
      axisLabel: { color: axisColor, interval: 0 },
      axisTick: { show: false },
      axisLine: { show: false },
      data: items.map(item => item.name)
    },
    tooltip: {
      trigger: 'axis',
      axisPointer: { type: 'shadow' }
    },
    series: [
      {
        type: 'bar',
        barMaxWidth: 18,
        showBackground: true,
        backgroundStyle: {
          color: isDark ? 'rgba(255,255,255,0.05)' : 'rgba(15,23,42,0.04)',
          borderRadius: 12
        },
        data: items.map(item => ({
          value: item.value,
          itemStyle: {
            borderRadius: [0, 12, 12, 0],
            color: createLinearGradient(1, 0, 0, 0, [
              { offset: 0, color: rgbaColor(themeColors.primary, 0.95) },
              { offset: 1, color: rgbaColor(themeColors.info, 0.55) }
            ])
          }
        }))
      }
    ]
  };
};

const getCourseResultsOption = (items, isDark) => {
  const axisColor = isDark ? '#dbe7ff' : '#475569';
  const splitLineColor = isDark ? 'rgba(255,255,255,0.08)' : 'rgba(15,23,42,0.07)';

  return {
    animationDuration: 1200,
    animationEasing: 'quarticOut',
    color: ['#2563eb', '#f97316'],
    legend: {
      top: 0,
      right: 0,
      icon: 'roundRect',
      textStyle: { color: axisColor }
    },
    tooltip: {
      trigger: 'axis',
      axisPointer: { type: 'shadow' }
    },
    grid: {
      left: 10,
      right: 10,
      top: 38,
      bottom: 12,
      containLabel: true
    },
    xAxis: {
      type: 'category',
      data: items.map(item => item.name || item.value),
      axisLabel: { color: axisColor, interval: 0 },
      axisTick: { show: false },
      axisLine: { show: false }
    },
    yAxis: {
      type: 'value',
      minInterval: 1,
      axisLabel: { color: axisColor },
      splitLine: { lineStyle: { color: splitLineColor } },
      axisTick: { show: false },
      axisLine: { show: false }
    },
    series: [
      {
        name: 'Natijalar',
        type: 'bar',
        barMaxWidth: 34,
        data: items.map(item => ({
          value: item.result_count || 0,
          itemStyle: {
            borderRadius: [12, 12, 4, 4],
            color: createLinearGradient(0, 0, 0, 1, [
              { offset: 0, color: '#38bdf8' },
              { offset: 1, color: '#2563eb' }
            ])
          }
        }))
      },
      {
        name: 'Signal',
        type: 'bar',
        barMaxWidth: 18,
        data: items.map(item => ({
          value: item.alert_count || 0,
          itemStyle: {
            borderRadius: [10, 10, 4, 4],
            color: createLinearGradient(0, 0, 0, 1, [
              { offset: 0, color: '#fb923c' },
              { offset: 1, color: '#ef4444' }
            ])
          }
        }))
      }
    ]
  };
};

const getNationalityResultsOption = (items, isDark) => {
  const textColor = isDark ? '#dbe7ff' : '#334155';

  return {
    animationDuration: 1300,
    animationEasing: 'quarticOut',
    color: ['#2563eb', '#0ea5e9', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#14b8a6', '#64748b'],
    tooltip: {
      trigger: 'item',
      formatter: params => `${params.name}<br/>${params.value} ta natija`
    },
    legend: {
      bottom: 0,
      type: 'scroll',
      icon: 'circle',
      itemWidth: 9,
      itemHeight: 9,
      textStyle: { color: textColor }
    },
    series: [
      {
        name: 'Millati',
        type: 'pie',
        radius: ['44%', '72%'],
        center: ['50%', '42%'],
        avoidLabelOverlap: true,
        itemStyle: {
          borderColor: isDark ? '#0f172a' : '#ffffff',
          borderWidth: 3,
          borderRadius: 7
        },
        label: {
          color: textColor,
          formatter: '{b}\n{c}',
          fontWeight: 700
        },
        labelLine: {
          lineStyle: {
            color: isDark ? 'rgba(226,232,240,0.35)' : 'rgba(15,23,42,0.18)'
          }
        },
        data: items.map(item => ({
          name: item.name,
          value: item.result_count || 0
        }))
      }
    ]
  };
};

const getCoverageOption = (coverage, isDark) => {
  const safeCoverage = Math.max(0, Math.min(100, Number(coverage || 0)));
  const remainder = Math.max(0, 100 - safeCoverage);
  const textColor = isDark ? '#f8fafc' : '#0f172a';
  const subColor = isDark ? '#cbd5e1' : '#64748b';

  return {
    animation: true,
    animationDuration: 1300,
    animationEasing: 'quarticOut',
    series: [
      {
        type: 'pie',
        radius: ['68%', '86%'],
        center: ['50%', '58%'],
        startAngle: 210,
        endAngle: -30,
        silent: true,
        label: { show: false },
        data: [
          {
            value: safeCoverage,
            itemStyle: {
              color: createLinearGradient(0, 0, 1, 1, [
                { offset: 0, color: '#10b981' },
                { offset: 1, color: '#06b6d4' }
              ]),
              borderRadius: 40
            }
          },
          {
            value: remainder,
            itemStyle: {
              color: isDark ? 'rgba(255,255,255,0.08)' : 'rgba(148,163,184,0.22)'
            }
          },
          {
            value: 26,
            itemStyle: { color: 'transparent' }
          }
        ]
      }
    ],
    graphic: [
      {
        type: 'text',
        left: 'center',
        top: '40%',
        style: {
          text: `${safeCoverage}%`,
          fill: textColor,
          fontSize: 34,
          fontWeight: 800
        }
      },
      {
        type: 'text',
        left: 'center',
        top: '56%',
        style: {
          text: 'Pasport qamrovi',
          fill: subColor,
          fontSize: 13,
          fontWeight: 700
        }
      }
    ]
  };
};

const getRiskLandscapeOption = (items, isDark) => {
  const categories = items.map(item => item.title);
  const values = items.map(item => item.count);
  const axisColor = isDark ? '#cbd5e1' : '#475569';

  return {
    animationDuration: 1200,
    animationEasing: 'quarticOut',
    grid: {
      left: 10,
      right: 10,
      top: 10,
      bottom: 28,
      containLabel: true
    },
    tooltip: {
      trigger: 'axis',
      axisPointer: { type: 'shadow' }
    },
    xAxis: {
      type: 'category',
      data: categories,
      axisLabel: {
        color: axisColor,
        interval: 0,
        fontSize: 11
      },
      axisTick: { show: false },
      axisLine: { show: false }
    },
    yAxis: {
      type: 'value',
      minInterval: 1,
      axisLabel: { color: axisColor },
      splitLine: {
        lineStyle: {
          color: isDark ? 'rgba(255,255,255,0.08)' : 'rgba(15,23,42,0.06)'
        }
      },
      axisTick: { show: false },
      axisLine: { show: false }
    },
    series: [
      {
        type: 'bar',
        barWidth: 30,
        itemStyle: {
          borderRadius: [14, 14, 4, 4]
        },
        label: {
          show: true,
          position: 'top',
          color: axisColor,
          fontWeight: 700
        },
        data: values.map((value, index) => {
          const palette = [
            ['#ef4444', '#fb7185'],
            ['#f59e0b', '#fbbf24'],
            ['#0ea5e9', '#38bdf8'],
            ['#2563eb', '#60a5fa']
          ];
          const [start, end] = palette[index % palette.length];

          return {
            value,
            itemStyle: {
              color: createLinearGradient(0, 0, 0, 1, [
                { offset: 0, color: end },
                { offset: 1, color: start }
              ])
            }
          };
        })
      }
    ]
  };
};

const Dashboard = () => {
  const { isDark, user, token, dashboardMeta, setDashboardMeta } = useContext(AppContext);
  const [data, setData] = useState(emptyState);
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState('');
  const endpointCandidates = useMemo(
    () => API_BASE_CANDIDATES.map(base => `${base}/p-admin/dashboard.php`),
    []
  );

  useEffect(() => {
    let mounted = true;

    const requestDashboard = async requestHeaders => {
      let lastError = null;

      for (const endpoint of endpointCandidates) {
        try {
          return await axios.get(endpoint, {
            timeout: 15000,
            headers: requestHeaders,
            withCredentials: true
          });
        } catch (error) {
          lastError = error;

          const status = error.response?.status || 0;
          if (status && ![404, 502, 503, 504].includes(status)) {
            throw error;
          }
        }
      }

      throw lastError || new Error('Dashboard endpointi topilmadi');
    };

    const fetchDashboard = async () => {
      const requestHeaders = getAuthHeaders();

      if (!user && !requestHeaders.Authorization) {
        setLoading(false);
        return;
      }

      setLoading(true);
      setErrorMessage('');

      try {
        const response = await requestDashboard(requestHeaders);

        if (!mounted) {
          return;
        }

        if (response.data && response.data.success) {
          setData(response.data);
          setDashboardMeta(current => ({
            ...(current || {}),
            user: response.data.user || current?.user || user
          }));
          setLoading(false);
          return;
        }

        setErrorMessage(response.data?.message || 'Dashboard ma`lumotlarini olib bo`lmadi.');
      } catch (error) {
        const message =
          error.response?.data?.message ||
          `Dashboard API bilan ulanishda xatolik yuz berdi. Tekshirildi: ${endpointCandidates.join(', ')}`;
        setErrorMessage(message);
      }

      if (mounted) {
        setLoading(false);
      }
    };

    fetchDashboard();

    return () => {
      mounted = false;
    };
  }, [endpointCandidates, setDashboardMeta, token, user]);

  const currentUser = data.user || dashboardMeta?.user || user;
  const canManageTests = hasPermission(currentUser, 'padmin.tests.manage');
  const canViewStudents = hasPermission(currentUser, 'padmin.students.view');
  const canViewPassport = hasPermission(currentUser, 'padmin.passport.view');
  const canViewAlerts = hasPermission(currentUser, 'padmin.alerts.view');
  const totalTemperamentEntries = (data.temperament || []).reduce((sum, item) => sum + item.value, 0);
  const totalRiskSignals = (data.risk_cards || []).reduce((sum, item) => sum + item.count, 0);
  const totalCourseResults = (data.course_results || []).reduce((sum, item) => sum + Number(item.result_count || 0), 0);
  const totalNationalityResults = (data.nationality_results || []).reduce(
    (sum, item) => sum + Number(item.result_count || 0),
    0
  );
  const activePeriod = data.period && data.period.selected ? data.period.selected : null;
  const activePeriodLabel = formatSelectedPeriod(data.period);
  const totalStudentNote =
    data.stats.total_students > 0
      ? `${data.stats.total_groups} ta guruh va ${data.stats.total_faculties} ta fakultet monitoringda.`
      : 'Hozircha faol talaba yozuvlari topilmadi.';
  const totalPassportNote =
    data.stats.total_students > 0
      ? `${data.stats.total_students} talabaning ${data.stats.total_passport_filled || 0} tasida pasport ma\`lumoti bor.`
      : 'Pasport qamrovi uchun faol talaba yozuvlari topilmadi.';
  const periodStudentsNote =
    data.stats.groups > 0
      ? `${activePeriodLabel || 'Tanlangan davr'} bo'yicha ${data.stats.groups} ta guruhda ${data.stats.students || 0} ta talaba bor.`
      : 'Tanlangan davrda test topshirgan talabalar topilmadi.';
  const periodResultsNote =
    data.stats.results > 0
      ? `${activePeriodLabel || 'Tanlangan davr'} bo'yicha ${data.stats.results || 0} ta natija yozilgan.`
      : 'Tanlangan davr bo`yicha diagnostika natijalari topilmadi.';

  const temperamentOption = useMemo(() => getTemperamentOption(data.temperament || [], isDark), [data.temperament, isDark]);
  const facultyOption = useMemo(() => getFacultyOption(data.faculties || [], isDark), [data.faculties, isDark]);
  const courseResultsOption = useMemo(
    () => getCourseResultsOption(data.course_results || [], isDark),
    [data.course_results, isDark]
  );
  const nationalityResultsOption = useMemo(
    () => getNationalityResultsOption(data.nationality_results || [], isDark),
    [data.nationality_results, isDark]
  );
  const coverageOption = useMemo(() => getCoverageOption(data.stats.passport_coverage, isDark), [data.stats.passport_coverage, isDark]);
  const riskLandscapeOption = useMemo(() => getRiskLandscapeOption(data.risk_cards || [], isDark), [data.risk_cards, isDark]);

  return (
    <div className="psi-dashboard">
      {errorMessage ? (
        <Alert color="warning" className="mb-4">
          {errorMessage}
        </Alert>
      ) : null}

      {loading ? (
        <div className="psi-loading-block">
          <Spinner color="primary" />
          <span>Dashboard ma`lumotlari yuklanmoqda...</span>
        </div>
      ) : null}

      <section className="psi-dashboard-intro">
        <div className="psi-dashboard-intro__copy">
          <span className="psi-dashboard-intro__eyebrow">Kompleks tahlil va monitoring tizimi</span>
          <h2>
            <span className="psi-dashboard-intro__title-line">Talabalarning psixologik holatini</span>
            <span className="psi-dashboard-intro__title-line psi-dashboard-intro__title-line--accent">
              chuqur baholash va dinamik kuzatish paneli
            </span>
          </h2>
          <p className="psi-dashboard-intro__lead">
            Tizim temperament, kommunikativlik, motivatsiya, bezovtalanish, nevrasteniya, munosabatlar diagnostikasi va
            kasbga moyillik yo`nalishlari asosida individual psixologik pasport yaratadi.
          </p>
          <div className="psi-dashboard-intro__badges">
            <span>Individual psixologik pasport</span>
            <span>Masofaviy test va so`rovnoma</span>
            <span>Dinamik monitoring</span>
            {activePeriod ? <span>{activePeriod.yil} / {activePeriod.chorak}-chorak</span> : null}
          </div>
        </div>
        <div className="psi-dashboard-intro__operator">
          <span>Mas`ul mutaxassis</span>
          <strong>{currentUser?.full_name || 'P-admin'}</strong>
          <small>{currentUser?.role_label || 'Psixologik boshqaruv'}</small>
          <div className="psi-dashboard-intro__operator-pills">
            <IntroStatPill
              label="Jami talaba"
              value={data.stats.total_students}
              suffix="+"
              icon="users"
              tone="primary"
              delay={0.18}
            />
            <IntroStatPill
              label="Pasport"
              value={data.stats.total_passport_coverage}
              suffix="%"
              icon="check-circle"
              tone="success"
              delay={0.26}
            />
            <IntroStatPill
              label="Davr natija"
              value={data.stats.results || 0}
              suffix="+"
              icon="clipboard-list"
              tone="info"
              delay={0.34}
            />
          </div>
        </div>
      </section>

      <Row>
        <Col xl={3} md={6} className="mb-4">
          <MetricCard
            label="Talabalar soni"
            value={data.stats.total_students}
            suffix=" ta"
            note={totalStudentNote}
            icon="users"
            tone="primary"
            accent={<span>{data.stats.students || 0} ta davr talaba</span>}
            index={0}
          />
        </Col>
        <Col xl={3} md={6} className="mb-4">
          <MetricCard
            label="Guruhlar soni"
            value={data.stats.total_groups}
            suffix=" ta"
            note={periodStudentsNote}
            icon="stream"
            tone="info"
            accent={<span>{data.stats.groups || 0} ta davr guruh</span>}
            index={1}
          />
        </Col>
        <Col xl={3} md={6} className="mb-4">
          <MetricCard
            label="Pasport to`ldirish"
            value={data.stats.total_passport_coverage}
            suffix="%"
            note={totalPassportNote}
            icon="check-circle"
            tone="success"
            accent={<span>{data.stats.passport_filled || 0} ta davr pasport</span>}
            index={2}
          />
        </Col>
        <Col xl={3} md={6} className="mb-4">
          <MetricCard
            label="Faol test fanlari"
            value={data.stats.subjects || 0}
            suffix=" ta"
            note={periodResultsNote}
            icon="book"
            tone="warning"
            accent={<span>{totalRiskSignals} ta jami signal</span>}
            index={3}
          />
        </Col>
      </Row>

      <Row>
        <Col xl={6} className="mb-4">
          <PanelCard
            title="Davrdagi pasport qamrovi"
            subtitle="Tanlangan davrda test topshirgan talabalar bo`yicha pasport to`ldirilishi."
            action={<Badge color="soft-success">{data.stats.passport_coverage}% tayyor</Badge>}
            className="psi-panel-card--equal"
          >
            <div className="psi-chart-wrap psi-chart-wrap--compact">
              <ReactEchartsCore echarts={echarts} option={coverageOption} style={{ height: '300px', width: '100%' }} />
            </div>
          </PanelCard>
        </Col>
        <Col xl={6} className="mb-4">
          <PanelCard
            title="Xavf landshafti"
            subtitle="Qaysi xavf segmentlari ustuvorligini ko`rsatadigan diagramma."
            action={<Badge color="soft-danger">{totalRiskSignals} ta signal</Badge>}
            className="psi-panel-card--equal"
          >
            <div className="psi-chart-wrap psi-chart-wrap--compact">
              <ReactEchartsCore
                echarts={echarts}
                option={riskLandscapeOption}
                style={{ height: '300px', width: '100%' }}
              />
            </div>
          </PanelCard>
        </Col>
      </Row>

      <Row>
        <Col xl={6} className="mb-4">
          <PanelCard
            title="Temperament taqsimoti"
            subtitle="Psixologik portretning asosiy temperament segmentlari."
            action={<Badge color="soft-primary">{totalTemperamentEntries} ta yozuv</Badge>}
            className="psi-panel-card--equal"
          >
            <div className="psi-chart-wrap psi-chart-wrap--compact">
              <ReactEchartsCore echarts={echarts} option={temperamentOption} style={{ height: '320px', width: '100%' }} />
            </div>
          </PanelCard>
        </Col>
        <Col xl={6} className="mb-4">
          <PanelCard
            title="Fakultetlar kesimi"
            subtitle="Tanlangan davrda test natijasi bor talabalar fakultetlar bo`yicha."
            className="psi-panel-card--equal"
          >
            <div className="psi-chart-wrap psi-chart-wrap--compact">
              <ReactEchartsCore echarts={echarts} option={facultyOption} style={{ height: '320px', width: '100%' }} />
            </div>
          </PanelCard>
        </Col>
      </Row>

      <Row>
        <Col xl={6} className="mb-4">
          <PanelCard
            title="Kurs kesimida natijalar tahlili"
            subtitle="Tanlangan davrdagi test natijalari va xavf signallari kurslar bo`yicha."
            action={<Badge color="soft-primary">{totalCourseResults} ta natija</Badge>}
            className="psi-panel-card--equal"
          >
            <div className="psi-chart-wrap psi-chart-wrap--compact">
              {(data.course_results || []).length ? (
                <ReactEchartsCore
                  echarts={echarts}
                  option={courseResultsOption}
                  style={{ height: '320px', width: '100%' }}
                />
              ) : (
                <div className="psi-empty-state">Kurs kesimida natija topilmadi.</div>
              )}
            </div>
          </PanelCard>
        </Col>
        <Col xl={6} className="mb-4">
          <PanelCard
            title="Millati kesimida natijalar tahlili"
            subtitle="Talabalar millati bo`yicha natija ulushi animatsion diagrammada."
            action={<Badge color="soft-info">{totalNationalityResults} ta natija</Badge>}
            className="psi-panel-card--equal"
          >
            <div className="psi-chart-wrap psi-chart-wrap--compact">
              {(data.nationality_results || []).length ? (
                <ReactEchartsCore
                  echarts={echarts}
                  option={nationalityResultsOption}
                  style={{ height: '320px', width: '100%' }}
                />
              ) : (
                <div className="psi-empty-state">Millati kesimida natija topilmadi.</div>
              )}
            </div>
          </PanelCard>
        </Col>
      </Row>

      <Row>
        <Col xl={8} className="mb-4">
          <PanelCard
            title="Diagnostika metodikalari"
            subtitle="Talabalarning psixologik holatini kompleks baholash uchun ishlatiladigan asosiy modul va testlar."
            className="psi-panel-card--equal"
          >
            <div className="psi-system-grid">
              {diagnosticModules.map((item, index) => (
                <SystemCard key={item.title} title={item.title} description={item.description} icon={item.icon} index={index} />
              ))}
            </div>
          </PanelCard>
        </Col>
        <Col xl={4} className="mb-4">
          <PanelCard title="Tezkor amallar" subtitle="Asosiy bo`limlarga tez kirish." className="psi-panel-card--equal">
            <div className="psi-action-grid psi-action-grid--stacked">
              {canViewPassport ? (
                <Link to="/psixologik-pasport" className="psi-action-tile">
                  <FontAwesomeIcon icon="check-circle" />
                  <strong>Psixologik pasport</strong>
                  <span>Talabalar kartalari va individual portret</span>
                </Link>
              ) : null}
              {canViewStudents ? (
                <Link to="/talabalar" className="psi-action-tile">
                  <FontAwesomeIcon icon="users" />
                  <strong>Talabalar bazasi</strong>
                  <span>Monitoringdagi talabalar va guruhlar ro`yxati</span>
                </Link>
              ) : null}
              {canViewAlerts ? (
                <Link to="/xavf-guruhlari/suitsidlar" className="psi-action-tile">
                  <FontAwesomeIcon icon="exclamation-triangle" />
                  <strong>Xavf signallari</strong>
                  <span>Ustuvor psixologik holatlar markazi</span>
                </Link>
              ) : null}
              {canManageTests ? (
                <Link to="/test-sozlamalari/test-qoshish" className="psi-action-tile">
                  <FontAwesomeIcon icon="tasks" />
                  <strong>Test qo`shish</strong>
                  <span>Custom test mavzulari, savollar va rasmli variantlar boshqaruvi</span>
                </Link>
              ) : null}
            </div>
          </PanelCard>
        </Col>
      </Row>

      <Row>
        <Col xl={5} className="mb-4">
          <PanelCard
            title="Platforma imkoniyatlari"
            subtitle="Psixologik xizmatlarni raqamlashtirish uchun asosiy imkoniyatlar."
            className="psi-panel-card--equal"
          >
            <div className="psi-capability-grid">
              {platformCapabilities.map((item, index) => (
                <CapabilityCard
                  key={item.title}
                  title={item.title}
                  description={item.description}
                  icon={item.icon}
                  index={index}
                />
              ))}
            </div>
          </PanelCard>
        </Col>
        <Col xl={7} className="mb-4">
          <PanelCard
            title="Ustuvor ogohlantirishlar"
            subtitle="Birinchi navbatda ko`rib chiqilishi kerak bo`lgan talabalar."
            action={<Badge color="soft-warning">{(data.priority_alerts || []).length} ta yozuv</Badge>}
            className="psi-panel-card--equal"
          >
            <div className="psi-alert-list">
              {(data.priority_alerts || []).length ? (
                data.priority_alerts.map(item => (
                  <div className="psi-alert-list__item psi-reveal" key={item.id}>
                    <div className="psi-alert-list__avatar">{item.full_name.charAt(0)}</div>
                    <div className="psi-alert-list__copy">
                      <strong>{item.full_name}</strong>
                      <span>
                        {item.faculty} • {item.group}-guruh
                      </span>
                    </div>
                    <Badge color={`soft-${item.tone}`}>{item.alert}</Badge>
                  </div>
                ))
              ) : (
                <div className="psi-empty-state">Hozircha ustuvor signal mavjud emas.</div>
              )}
            </div>
          </PanelCard>
        </Col>
      </Row>

      <Row>
        <Col xl={12} className="mb-4">
          <PanelCard
            title="Tanlangan davrdagi talabalar"
            subtitle={
              activePeriodLabel
                ? `${activePeriodLabel} bo'yicha test natijasi bor talabalar ro'yxati.`
                : 'Tanlangan davr bo`yicha test natijasi bor talabalar ro`yxati.'
            }
            action={canManageTests ? <Link to="/test-sozlamalari/davr" className="psi-inline-link">Davr sozlamasi</Link> : null}
            className="psi-panel-card--equal"
          >
            <div className="table-responsive">
              <table className="table psi-table mb-0">
                <thead>
                  <tr>
                    <th>F.I.O.</th>
                    <th>Fakultet</th>
                    <th>Guruh</th>
                    <th>Kurs</th>
                    <th>Millati</th>
                    <th>Natija</th>
                    <th>Signal</th>
                  </tr>
                </thead>
                <tbody>
                  {(data.recent_students || []).length ? (
                    (data.recent_students || []).map(item => (
                      <tr key={item.id}>
                        <td>{item.full_name}</td>
                        <td>{item.faculty}</td>
                        <td>{item.group}</td>
                        <td>{item.course || '-'}</td>
                        <td>{item.nationality || '-'}</td>
                        <td>{item.result_count} ta</td>
                        <td>{item.alert_count || 0} ta</td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="7" className="text-center text-500 py-4">
                        Tanlangan davr bo`yicha talaba yozuvlari topilmadi.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </PanelCard>
        </Col>
      </Row>
    </div>
  );
};

export default Dashboard;
